// WHAT: Tab component for displaying Yazio meal logging data.
// WHY:  Provides interface for viewing consumed meals and nutrition items from Yazio integration, grouped by meal time with auto-loading.
// DOMAIN: Health → Yazio
// DEPENDENCIES: React, ../../hooks/useYazioConsumedItems, @/components/ui/Button, @/shared/utils
// RISKS:
//   - WARN: Auto-loading on mount depends on useYazioConsumedItems hook; ensure credentials are configured.
//   - NOTE: Meal grouping by daytime assumes consistent data structure; may fail with unexpected formats.
//   - NOTE: Error handling redirects to settings; ensure settings route exists.

import { useEffect } from "react";

import { Button } from "@/components/ui/Button";

import { useYazioConsumedItems } from "../../hooks/use-yazio-consumed-items";

/**
 * WHAT:
 *   Tab component for displaying Yazio meal logging data.
 *
 * WHY:
 *   Provides users with a view of their consumed meals and nutrition items from the Yazio integration,
 *   organized by meal time (breakfast, lunch, dinner, snacks) with automatic data loading.
 *
 * BEHAVIOR:
 *   - Automatically loads today's meal data on mount if not already loaded
 *   - Displays loading state with spinner during data fetch
 *   - Shows error state with retry and dismiss options
 *   - Renders empty state with configuration link when no data is available
 *   - Groups consumed items by meal time (breakfast, lunch, dinner, snacks)
 *   - Displays meal cards with nutrition information and timestamps
 *   - Provides refresh functionality to reload meal data
 *
 * RISKS:
 *   - WARN: Auto-loading on mount depends on useYazioConsumedItems hook; ensure credentials are configured.
 *   - NOTE: Meal grouping by daytime assumes consistent data structure; may fail with unexpected formats.
 *   - NOTE: Error handling redirects to settings; ensure settings route exists.
 *   - PERF: Grouping and mapping operations may impact render performance with many items.
 *
 * @returns {JSX.Element} Yazio meal logging tab component
 *
 */
export function YazioMealLoggingTab() {
  const { clearError, data, error, isLoading, loadTodayData, refreshData } =
    useYazioConsumedItems();

  /**
   * WHAT:
   *   Effect hook to automatically load today's meal data on component mount.
   *
   * WHY:
   *   Ensures meals are displayed immediately when the tab is opened without requiring manual refresh.
   *
   * SIDE EFFECTS:
   *   - Calls loadTodayData if data is not already loaded and no error exists
   *
   * RISKS:
   *   - NOTE: Dependency on loadTodayData; ensure it's stable or memoized to prevent infinite loops.
   *   - WARN: Only loads if no data, not loading, and no error; may not retry after errors.
   */
  useEffect(() => {
    if (!data && !isLoading && !error) {
      loadTodayData();
    }
  }, [data, isLoading, error, loadTodayData]);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <svg
              className="w-6 h-6 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-red-400">Error Loading Meal Data</h3>
              <p className="text-red-300 mt-1">{error}</p>
            </div>
          </div>
          <div className="flex space-x-3 mt-4">
            <Button onClick={refreshData} size="sm" variant="outline">
              Try Again
            </Button>
            <Button onClick={clearError} size="sm" variant="outline">
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-500 border-t-transparent mx-auto mb-4" />
            <p className="text-slate-400">Loading your meal data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 text-center">
          <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Meal Data</h3>
          <p className="text-slate-400 mb-6">
            No meal data found for today. Make sure your Yazio credentials are configured.
          </p>
          <div className="flex space-x-3 justify-center">
            <Button onClick={loadTodayData} variant="primary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              Try Again
            </Button>
            <Button onClick={() => (globalThis.location.href = "/settings")} variant="outline">
              Configure Credentials
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Group items by daytime
  const groupedItems: Record<string, typeof data.items> = {};
  for (const item of data.items) {
    const daytime = item.daytime;
    if (daytime) {
      groupedItems[daytime] ??= [];
      groupedItems[daytime].push(item);
    }
  }

  const daytimeLabels = {
    breakfast: "Breakfast",
    dinner: "Dinner",
    lunch: "Lunch",
    snack: "Snacks",
  };

  const daytimeIcons = {
    breakfast: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    dinner: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    lunch: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    snack: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
  };

  let totalCalories = 0;
  for (const item of data.items) {
    totalCalories += item.calories;
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Meal Logging</h2>
          <p className="text-slate-400 mt-1">
            Your logged meals for {new Date(data.date).toLocaleDateString("de-DE")}
          </p>
        </div>
        <Button disabled={isLoading} onClick={refreshData} size="sm" variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          Refresh
        </Button>
      </div>

      {/* Total Calories Summary */}
      <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Total Logged Calories</h3>
            <p className="text-slate-400">All meals combined</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-orange-400">{totalCalories.toFixed(0)}</div>
            <div className="text-sm text-slate-400">kcal</div>
          </div>
        </div>
      </div>

      {/* Meals by Daytime */}
      <div className="space-y-6">
        {Object.entries(groupedItems).map(([daytime, items]) => {
          let daytimeCalories = 0;
          for (const item of items) {
            daytimeCalories += item.calories;
          }
          const label = daytimeLabels[daytime as keyof typeof daytimeLabels] ?? daytime;
          const icon = daytimeIcons[daytime as keyof typeof daytimeIcons];

          return (
            <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700" key={daytime}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <div className="text-orange-400">{icon}</div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{label}</h3>
                    <p className="text-sm text-slate-400">{items.length} items</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-white">
                    {daytimeCalories.toFixed(0)} kcal
                  </div>
                </div>
              </div>

              {items.length > 0 ? (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div
                      className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      key={item.id}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{item.name}</h4>
                        <div className="flex items-center space-x-4 text-sm text-slate-400 mt-1">
                          <span>
                            {item.amount.toFixed(1)} {item.serving ?? "g"}
                          </span>
                          {item.servingQuantity ? (
                            <span>({item.servingQuantity.toFixed(1)} servings)</span>
                          ) : null}
                          <span className="capitalize">{item.type}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-white">
                          {item.calories.toFixed(0)} kcal
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <p>No items logged for {label.toLowerCase()}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Card */}
      <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">About Meal Logging</h3>
            <p className="text-slate-400">
              This shows all the meals and food items you&apos;ve logged in the Yazio app for today.
              To add new meals or modify existing ones, use the Yazio app directly. This view
              provides a comprehensive overview of your daily nutrition intake.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
